import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, ArrowRight, User } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useBookCall } from "@/components/BookCallContext";
import { TESTIDS } from "@/constants/testIds";

const QR = [
    { id: "explore", label: "Explore use cases" },
    { id: "discuss", label: "Discuss my requirement" },
    { id: "book", label: "Book a demo" },
];

export default function ChatWidget() {
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [handoff, setHandoff] = useState(false);
    const bodyRef = useRef(null);
    const navigate = useNavigate();
    const { openModal } = useBookCall();

    // Lazy start once opened
    useEffect(() => {
        if (open && messages.length === 0) {
            const t = setTimeout(
                () => setMessages([{ from: "bot", text: "Hi 👋 What are you looking for?", quick: QR }]),
                0,
            );
            return () => clearTimeout(t);
        }
    }, [open, messages.length]);

    useEffect(() => {
        if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }, [messages]);

    const reply = (id, label) => {
        setMessages((m) => [...m, { from: "user", text: label }]);
        setTimeout(() => {
            let text = "Got it, let me connect you to a human who can help.";
            if (id === "explore") text = "Great, browsing our use cases. Want a human for specifics?";
            if (id === "book") text = "Perfect, opening the demo form. A human will confirm your slot.";
            setMessages((m) => [
                ...m,
                { from: "bot", text, handoff: true },
            ]);
            if (id === "book") setTimeout(() => openModal({ source: "chat" }), 350);
            if (id === "explore") setTimeout(() => navigate("/use-cases"), 350);
        }, 400);
    };

    const toHuman = () => {
        setHandoff(true);
        setMessages((m) => [
            ...m,
            {
                from: "bot",
                text: "Connecting you to a team member… they'll reply here shortly. Or call ☏ +91 97692 32430 or write to hello@kyrolabs.ai.",
            },
        ]);
    };

    return (
        <>
            <button
                data-testid={TESTIDS.chat.bubble}
                onClick={() => setOpen((v) => !v)}
                aria-label="Open chat"
                style={{
                    position: "fixed",
                    right: 24,
                    bottom: 64,
                    width: 58,
                    height: 58,
                    borderRadius: "50%",
                    background: "var(--accent)",
                    color: "#fff",
                    border: "none",
                    cursor: "pointer",
                    boxShadow: "0 14px 30px rgba(26,86,219,0.45)",
                    zIndex: 150,
                    display: "grid",
                    placeItems: "center",
                    transition: ".2s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.06)")}
                onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
            >
                {open ? <X size={22} /> : <MessageCircle size={22} />}
            </button>

            <AnimatePresence>
                {open && (
                    <motion.div
                        key="chat-panel"
                        data-testid={TESTIDS.chat.panel}
                        initial={{ opacity: 0, y: 12, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 12, scale: 0.97 }}
                        transition={{ duration: 0.2 }}
                        style={{
                            position: "fixed",
                            right: 24,
                            bottom: 134,
                            width: 340,
                            maxWidth: "calc(100vw - 36px)",
                            background: "#0e1116",
                            color: "var(--ink)",
                            border: "1px solid var(--line-strong)",
                            borderRadius: 18,
                            boxShadow: "0 26px 60px rgba(0,0,0,0.55)",
                            zIndex: 150,
                            overflow: "hidden",
                        }}
                    >
                        <div
                            style={{
                                background: "linear-gradient(135deg, #1a2b5c, #0e1d44)",
                                color: "#fff",
                                padding: "13px 18px",
                                fontFamily: "Bricolage Grotesque",
                                fontWeight: 600,
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                borderBottom: "1px solid var(--line-strong)",
                            }}
                        >
                            <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                {handoff ? (
                                    <>
                                        <User size={14} /> KyroLabs · human soon
                                    </>
                                ) : (
                                    <>KyroLabs assistant</>
                                )}
                            </span>
                            <button
                                data-testid={TESTIDS.chat.close}
                                onClick={() => setOpen(false)}
                                style={{
                                    background: "transparent",
                                    border: "none",
                                    color: "#fff",
                                    cursor: "pointer",
                                    opacity: 0.85,
                                }}
                                aria-label="Close"
                            >
                                <X size={16} />
                            </button>
                        </div>

                        <div
                            ref={bodyRef}
                            style={{ padding: 14, maxHeight: 320, overflowY: "auto" }}
                        >
                            {messages.map((m, i) => (
                                <div key={i} style={{ marginBottom: 10 }}>
                                    <div
                                        style={{
                                            borderRadius: 12,
                                            padding: "10px 12px",
                                            fontSize: ".86rem",
                                            maxWidth: "85%",
                                            ...(m.from === "user"
                                                ? {
                                                      background: "var(--accent)",
                                                      color: "#08090c",
                                                      marginLeft: "auto",
                                                      fontWeight: 500,
                                                  }
                                                : {
                                                      background: "rgba(255,255,255,0.04)",
                                                      color: "var(--ink)",
                                                      border: "1px solid var(--line)",
                                                  }),
                                        }}
                                    >
                                        {m.text}
                                    </div>
                                    {m.quick && (
                                        <div style={{ display: "grid", gap: 6, marginTop: 8 }}>
                                            {m.quick.map((q) => (
                                                <button
                                                    key={q.id}
                                                    data-testid={TESTIDS.chat.qr(q.id)}
                                                    onClick={() => reply(q.id, q.label)}
                                                    style={qrStyle}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.borderColor = "var(--accent)";
                                                        e.currentTarget.style.color = "var(--accent)";
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.borderColor = "var(--line-strong)";
                                                        e.currentTarget.style.color = "var(--ink)";
                                                    }}
                                                >
                                                    <span>{q.label}</span>
                                                    <ArrowRight size={13} />
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    {m.handoff && !handoff && (
                                        <button
                                            data-testid={TESTIDS.chat.toHuman}
                                            onClick={toHuman}
                                            style={{ ...qrStyle, marginTop: 8 }}
                                        >
                                            💬 Talk to a human
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}

const qrStyle = {
    textAlign: "left",
    background: "rgba(255,255,255,0.03)",
    border: "1px solid var(--line-strong)",
    borderRadius: 10,
    padding: "9px 12px",
    fontFamily: "inherit",
    fontSize: ".84rem",
    cursor: "pointer",
    transition: ".16s",
    color: "var(--ink)",
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
};
